<template>
	<div class="dynamic-item-list">
		<div class="row" v-if="itemsCount > 0">
			<div class="col-12">
				<slot name="heading"></slot>
			</div>

			<div class="col-12">
				<carousel :items-per-page="itemsPerPage" ref="carousel">
					<template slot="items" v-for="(item, index) in items">
						<category-item
							:key="item.id"
							:item-data="item.data"
							:decimal-count="$ceres.config.item.storeSpecial"
							:disable-carousel-on-mobile="items.length > itemsPerPage"
							:padding-classes="paddingClasses"
							:padding-inline-styles="paddingInlineStyles"
							:overwrite-url="overwriteUrl"
							:enable-siblings-call="enableSiblingsCall"
							:image-options="imageOptions"
							initiator="itemList"
							:is-first="index === 0"
							:sibling-item-list="index === 0 ? siblingItemList : {}"
							:enable-atc="enableAtc"
						>
							<template #before-prices>
								<div v-html="getContainerContentById(item.id, 'beforePrices')"></div>
							</template>
							<template #after-prices>
								<div v-html="getContainerContentById(item.id, 'afterPrices')"></div>
							</template>
							<template #ratings>
								<div v-html="getContainerContentById(item.id, 'ratings')"></div>
							</template>
						</category-item>
					</template>
				</carousel>
			</div>
		</div>
		<div class="row" v-else-if="showLoading && isLoading">
			<div class="col-12">
				<loading-animation class="cmp cmp-product-thumb"></loading-animation>
			</div>
		</div>
		<component v-if="loadSubComponent" :is="subComponent" @init="init" :settings="settings" :params="params"></component>
	</div>
</template>

<script>
import { mapState } from "vuex";
import { isDefined } from "../../../../ceres/helper/utils";

export default {
	props: {
		itemListKey: {
			type: String,
			required: true
		},
		params: {
			type: Object,
			default: () => {}
		},
		settings: {
			type: Object,
			default: () => {}
		},
		itemsPerPage: {
			type: Number,
			default: 4
		},
		maxItems: {
			type: Number,
			default: 4
		},
		paddingClasses: {
			type: String,
			default: null
		},
		paddingInlineStyles: {
			type: String,
			default: null
		},
		enableSiblingsCall: {
			type: Boolean,
			default: true
		},
		overwriteUrl: {
			type: String,
			default: ""
		},
		imageOptions: {
			type: Object,
			default() {
				return {};
			}
		},
		showLoading: {
			type: Boolean,
			default: false
		},
		enableAtc: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isInitialized: false,
			subComponent: "",
			containerKey: ""
		};
	},
	created() {
		let componentTag = "dynamic-item-list";
		componentTag +=
			([
				"cross_selling",
				"tag_item",
				"variation_relation_viewed_also_viewed",
				"variation_relation_viewed_also_bought",
				"variation_relation_bought_also_bought",
				"attribute_crossselling"
			].includes(this.itemListKey)
				? "-item"
				: "") +
			(["tag_basket", "prop_basket", "simple_basket"].includes(this.itemListKey) ? "-basket" : "") +
			(["tag_confirmation"].includes(this.itemListKey) ? "-confirmation" : "");
		componentTag +=
			([
				"variation_relation_viewed_also_viewed",
				"variation_relation_viewed_also_bought",
				"variation_relation_bought_also_bought"
			].includes(this.itemListKey)
				? "-variation-session"
				: "") +
			(this.itemListKey === "attribute_crossselling" ? "-attr-cross" : "") +
			(["tag_basket", "tag_item"].includes(this.itemListKey) ? "-tag" : "") +
			(this.itemListKey === "prop_basket" ? "-prop" : "") +
			(this.itemListKey === "simple_basket" ? "-simple" : "");
		this.subComponent = componentTag !== "dynamic-item-list" ? componentTag : "dynamic-item-list-common";
		
		if (this.itemListKey === "simple" && Object.keys(this.params).includes("variationIDs")) {
			this.containerKey = this.itemListKey + "-" + this.params.variationIDs[0];
		} else {
			this.containerKey = this.itemListKey;
		}
	},
	computed: {
		items() {
			return this.isInitialized ? this.$store.getters[`${this.containerKey}/getItemList`](this.maxItems) : [];
		},
		containers() {
			return this.isInitialized ? this.$store.getters[`${this.containerKey}/getContainers`] : {};
		},
		itemsCount() {
			return this.isInitialized ? this.$store.getters[`${this.containerKey}/getItemCount`] : 0;
		},
		isLoading() {
			return this.isInitialized ? this.$store.getters[`${this.containerKey}/isLoading`] : true;
		},
		loadSubComponent() {
			return this.itemListKey.includes("basket") ? this.isBasketInitiallyLoaded : true;
		},
		siblingItemList() {
			const siblingItemList = {};
			for (let item of this.items) {
				siblingItemList[item.data.item.id] = item.data.variation.id;
			}
			return siblingItemList;
		},
		
		...mapState({
			isBasketInitiallyLoaded: state => state.basket.isBasketInitiallyLoaded
		})
	},

	methods: {
		init(payload) {
			const isPassive =
				isDefined(this.settings) &&
				isDefined(this.settings.variationSessions) &&
				isDefined(this.settings.variationSessions.isPassive)
					? this.settings.variationSessions.isPassive
					: false;
			let sendingPayload = {
				key: this.itemListKey,
				isPassive,
				params: {
					overwriteUrl: this.overwriteUrl,
					imageOptions: this.imageOptions,
					urlAccessor: this.urlAccessor,
					items: this.maxItems ?? 20,
					...this.params
				}
			};
			
			if (isDefined(payload)) {
				sendingPayload = {
					...sendingPayload,
					...payload
				}
			}

			this.isInitialized = true;
			this.$store.dispatch("initDynamicItemList", sendingPayload);
		},
		getContainerContentById(variationId, containerKey) {
			const containersById = this.containers[variationId];
			if (isDefined(containersById)) {
				const container = containersById[containerKey];
				if (isDefined(container)) {
					return container;
				}
			}
			return "";
		}
	}
};
</script>